/**
 * Routes derived from the design system's own `NAV`, not written out a second time.
 *
 * `Sidebar.jsx` exports `NAV` — the fourteen entries in the four groups
 * `UI-Design-Spec.md#navigation` specifies, with the `g`-then-letter accelerators already
 * attached. Re-declaring that list here would create two lists that drift, and the way
 * they drift is the worst one available: a sidebar entry that navigates nowhere, or a
 * route with no way to reach it. So the route table *is* the nav table.
 *
 * ⚠ **One group is added here rather than read from `NAV`** — `STOCKS`, see below. It is added to
 * the *same* list rather than kept beside it, so the invariant above survives the addition: there
 * is still exactly one place that says which screens exist.
 *
 * The URL is `/` + the nav id, so `g` then `m` and a typed `/markets` are the same place.
 * That matters more than it looks: the operator reaches for a URL during an incident, and
 * a SPA that cannot be deep-linked is one where "send me the screen you are looking at"
 * has no answer.
 */

import { NAV } from '../design/components/chrome/Sidebar.jsx';

export interface NavItem {
  id: string;
  label: string;
  icon: string;
  key?: string;
  glyph?: string;
}

export interface NavGroup {
  group: string | null;
  items: NavItem[];
}

/**
 * The Stocks group, and it is declared **here** rather than in the vendored `NAV`.
 *
 * `web/src/design/` is under a SHA-256 manifest that `make design-verify` checks, so
 * `Sidebar.jsx` cannot gain three entries without failing CI — and the group is an app
 * addition rather than a design-system one: `03-Architecture/Stocks/API-and-UI.md` specifies it,
 * and `VENDOR.md`'s procedure is to push it upstream once it has been seen in the running app.
 * Until then the route table is still one list, which is the property this module exists to keep.
 *
 * 🔴 **Three entries and not one screen with tabs**, against the UI spec's own test:
 *
 * - **Screener** is the daily unit of work, and it carries the group's only badge — the count of
 *   *actionable* verdicts, `BUY + SELL + REVIEW`. Never `HOLD`, which is the absence of a claim
 *   and would make the badge read high on the calmest days.
 * - **Scorecard** because the screen that says the system is not working must not be reachable
 *   only through the screen that says it is. Same argument that put Wallet at top level.
 * - **Coverage** because source health and resolution failures are an operational responsibility
 *   with their own failure modes — distinct from Intel, which watches crypto sources with
 *   entirely different symptoms.
 *
 * ⚠ **Company is deliberately not here.** It is a detail route reached from a row
 * (`/screener/:isin`), and Watchlist is a saved filter on Screener rather than a screen: neither
 * has a responsibility of its own.
 *
 * The accelerators are `g k`, `g c` and `g v` — the letters `s`, `c` and `o` were taken by
 * Strategies, and `s` for Screener beside `s` for Strategies is the collision most likely to send
 * an operator to the wrong screen during an incident.
 */
const STOCKS: NavGroup = {
  group: 'STOCKS',
  items: [
    { id: 'screener', label: 'Screener', icon: 'search', key: 'k' },
    // 🔴 Above Scorecard because it is the only screen here that can be minutes old. The
    // others report a run that happened before the open; this one reports what is happening.
    // Accelerator `n` — `s` and `c` were already taken by Strategies and Scorecard.
    { id: 'news', label: 'News', icon: 'newspaper', key: 'n' },
    // 🔴 Directly under News because it is the same *kind* of surface — something said today,
    // against what the price did — and distinct from it because the unit is a statement rather
    // than a company's news. Measured 2026-08-20: a name filter on the News feed is under 40%
    // precise, and 6 of 22 real statements name no company at all, so neither the query nor the
    // row shape of that screen can carry this one. See `jmt_stocks/trump.py`.
    //
    // ⚠ Accelerator `t`, and it is arbitrary rather than mnemonic: every letter of "Trump" is
    // already claimed — `t` was free only because `p` Portfolio, `r` Research, `u`/`m` and `y`
    // System were checked against the vendored `NAV` first. Free letters remain `a h j q u x z`.
    //
    // ⚠ **The id is `trump` everywhere**, not just the label. D79 renamed the URL, the API paths
    // (`/stocks/trump`), both tables (`stock_trump_*`) and the module (`jmt_stocks/trump.py`) in
    // one revision, so there is a single spelling to grep for. The `policy_class` COLUMN keeps its
    // name deliberately — there it means which policy *area* a statement is about.
    { id: 'trump', label: 'Trump Watch', icon: 'landmark', key: 't' },
    { id: 'scorecard', label: 'Scorecard', icon: 'target', key: 'c' },
    { id: 'coverage', label: 'Coverage', icon: 'database', key: 'v' },
  ],
};

/**
 * `RUN · DECIDE · STOCKS · CAPITAL · —`, in that order.
 *
 * Spliced after `DECIDE` rather than appended, because the grouping is the order of the morning:
 * what is running, what to decide, what the equities side says, then the money. Located by the
 * group's *name* rather than by index, so a change to the vendored `NAV` moves this with it
 * instead of silently inserting in the wrong place.
 */
function withStocks(groups: NavGroup[]): NavGroup[] {
  const after = groups.findIndex((group) => group.group === 'DECIDE');
  if (after < 0) return [...groups, STOCKS];
  return [...groups.slice(0, after + 1), STOCKS, ...groups.slice(after + 1)];
}

export const NAV_GROUPS = withStocks(NAV as NavGroup[]);

/** The nav ids the Stocks section owns. The shell reads it; see `AppShell` for why. */
export const STOCK_SCREENS: ReadonlySet<string> = new Set(STOCKS.items.map((item) => item.id));

export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((group) => group.items);

/** `dashboard` — the screen a bare `/` redirects to. First entry of the first group. */
export const DEFAULT_SCREEN: string = NAV_ITEMS[0]?.id ?? 'dashboard';

export function pathFor(id: string): string {
  return `/${id}`;
}

/** The nav id for a pathname, or `undefined` for a path that is not a screen. */
export function screenFor(pathname: string): string | undefined {
  const id = pathname.replace(/^\/+/, '').split('/')[0];
  return NAV_ITEMS.some((item) => item.id === id) ? id : undefined;
}

/** `{ d: 'dashboard', s: 'strategies', … }` — the `g`-then-letter accelerators. */
export const ACCELERATORS: Record<string, string> = Object.fromEntries(
  NAV_ITEMS.filter((item) => item.key).map((item) => [item.key as string, item.id]),
);

export function titleFor(id: string | undefined): string {
  return NAV_ITEMS.find((item) => item.id === id)?.label ?? 'Not found';
}
